// <room-availability> Web Component
//
// PR-room.1 of the room-availability decouple
// (memory/room_availability_decouple_plan.md v3 §2). On
// /property/<slug>, this element wraps the rate-card / room-list
// section and fetches the rendered HTML fragment from
// GET /api/availability/<slug> on connect. innerHTMLs the response;
// dispatches `rooms-loaded` for downstream property.js binders
// (reserve button, room modal).
//
// PR-room.1 ships the element registered + smoke-testable but does
// NOT wire it into property.html yet — that's PR-room.2. This module
// only loads if base.html includes its <script> tag, which is added
// in this PR for early availability but stays inert until the
// element appears in some template.
//
// Usage (PR-room.2 will wire this into property.html, replacing the
// SSR rate-card section):
//   <room-availability
//       property-slug="wit-hotel-242392"
//       property-id="242392"
//       data-no-dates>     <!-- attr present iff no checkin/checkout -->
//     <div data-rooms-skeleton>Loading availability…</div>
//   </room-availability>
//   <template id="room-availability-error">
//     <!-- booking-request-fallback markup, cloned into element on fetch error -->
//   </template>
//
// The element forwards window.location.search verbatim to the
// endpoint — any combination of checkin/checkout/adults/children/
// repeated `age` params flows through, preserving the param shape
// PropertyPage already expects.
//
// One <room-availability> per page assumed (the room-groups-data
// JSON marker uses id=room-groups-data which is per-document
// unique). If multiple instances are ever needed, scope the parsing
// via event.target instead of document.

(function () {
    function _renderError(host) {
        const tpl = document.getElementById('room-availability-error');
        if (tpl && tpl.content) {
            host.innerHTML = '';
            host.appendChild(tpl.content.cloneNode(true));
            // PR-cf.cache.collapse: SSR emits the booking-request CTA + banner
            // unconditionally inside the error template. The {{if .HasDates}}
            // gate moved here so the cached HTML is query-invariant — remove
            // the CTA / banner when the visitor has no URL dates.
            if (!window.hasDates) {
                host.querySelectorAll('[data-needs-dates]').forEach(function (n) {
                    n.remove();
                });
            }
        } else {
            // SSR didn't emit the fallback template; render a minimal
            // generic error so users aren't stuck on the skeleton.
            host.innerHTML = '<div class="text-sm text-gray-500 py-12 text-center">Availability is temporarily unavailable. Please try again.</div>';
        }
    }

    function _renderNoDates(host) {
        // PR-cf.cache.collapse: read the localized prompt from the
        // SSR-emitted `data-no-dates-label` attribute on the host element
        // (falls back to English for safety). Previously hardcoded English
        // which was a pre-existing i18n bug — closed here as a side-effect.
        const label = host.getAttribute('data-no-dates-label') || 'Select dates to see availability and prices.';
        const div = document.createElement('div');
        div.className = 'text-sm text-gray-600 py-8 text-center';
        div.textContent = label;
        host.innerHTML = '';
        host.appendChild(div);
    }

    function _hydrateRoomGroupsData(host) {
        // <script type="application/json" id="room-groups-data">…</script>
        // is emitted by the fragment. innerHTML doesn't execute
        // scripts, but the textContent is preserved — parse manually
        // and assign to the global the legacy property.js consumers
        // (openRoomModal, navigateToRequestCheckout) read.
        const dataScript = host.querySelector('#room-groups-data');
        if (!dataScript) {
            window.roomGroupsData = [];
            return;
        }
        try {
            window.roomGroupsData = JSON.parse(dataScript.textContent || '[]');
        } catch (e) {
            console.error('room-availability: failed to parse #room-groups-data', e);
            window.roomGroupsData = [];
        }
    }

    function _readHasLiveRooms(host) {
        const marker = host.querySelector('[data-has-live-rooms]');
        return !!(marker && marker.dataset.hasLiveRooms === 'true');
    }

    function _dispatchRoomsLoaded(host, hasLiveRooms) {
        // rooms-loaded fires on document so property.js can listen
        // without holding a reference to the element. detail.hasLiveRooms
        // is included for any listener that wants to differentiate
        // (e.g. CTA copy, telemetry); property.js's reserve-button
        // handler treats the gate as binary — clears data-needs-rooms
        // on every outcome (live / cached-fallback / error) and lets
        // handleReserveClick auto-route per flow (direct-booking →
        // navigateToRequestCheckout when no .room-select; affiliate →
        // booking.com).
        document.dispatchEvent(new CustomEvent('rooms-loaded', {
            bubbles: true,
            detail: {
                hasLiveRooms: hasLiveRooms,
                propertyId: host.getAttribute('property-id') || '',
            },
        }));
    }

    class RoomAvailability extends HTMLElement {
        connectedCallback() {
            if (this._wiredOnce) return;
            this._wiredOnce = true;
            this._slug = this.getAttribute('property-slug') || '';
            this._propertyId = this.getAttribute('property-id') || '';
            this._noDates = this.hasAttribute('data-no-dates');

            // Per-element readyState-aware gate. The data-no-dates and
            // error paths do synchronous innerHTML writes that race the
            // parser if fired before SSR skeleton children are appended.
            // setTimeout(0) (the previous fix) proved insufficient when
            // the same pattern was exercised under cache-hit conditions
            // in <user-menu>. DCL gating is robust: defers until full
            // document parsed, OR runs immediately for dynamic insertions
            // (innerHTML inserts children atomically before custom-element
            // reactions fire). memory/hydration_race_fixes_plan.md §3.C.
            const self = this;
            const run = function () { self._hydrate(); };
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', run, { once: true });
            } else {
                run();
            }
        }

        _hydrate() {
            if (this._noDates) {
                _renderNoDates(this);
                _dispatchRoomsLoaded(this, false);
                return;
            }
            if (!this._slug) {
                _renderError(this);
                _dispatchRoomsLoaded(this, false);
                return;
            }
            // window.location.search forwards verbatim — preserves
            // multi-value `age` params + any future param additions
            // to PropertyPage's URL contract.
            const url = '/api/availability/' + encodeURIComponent(this._slug) + window.location.search;
            const self = this;
            fetch(url, { credentials: 'same-origin' })
                .then(function (resp) {
                    if (resp.status !== 200) {
                        return Promise.reject(new Error('status ' + resp.status));
                    }
                    return resp.text();
                })
                .then(function (html) {
                    self.innerHTML = html;
                    _hydrateRoomGroupsData(self);
                    const hasLive = _readHasLiveRooms(self);
                    _dispatchRoomsLoaded(self, hasLive);
                })
                .catch(function (err) {
                    console.error('room-availability: fetch failed', err);
                    _renderError(self);
                    _dispatchRoomsLoaded(self, false);
                });
        }
    }

    if (!customElements.get('room-availability')) {
        customElements.define('room-availability', RoomAvailability);
    }
})();
